/**
 * AP recurring-bill-template error catalog — stable codes shared by API, worker and web.
 *
 * Convention mirrors `./currency-errors.ts`: the codes live here, the pt-BR
 * `userMessage`/`recoveryHint` copy lives next to the throw site
 * (`packages/services/src/vendor/recurring-template.errors.ts`), and the HTTP
 * rendering lives in `apps/api/src/modules/ap/recurring-template-exception.filter.ts`.
 *
 * Deliberate deviation from the currency catalog: there is NO mirrored web copy
 * table. Every code below is thrown as a structured body carrying `userMessage`
 * and `recoveryHint`, and `getErrorMessage` (`apps/web/src/api/errors.ts`) already
 * renders those for any status < 500. A second copy of the strings would only
 * drift.
 *
 * Classify on these codes and on `Prisma.PrismaClientKnownRequestError.meta.target`
 * — never on message substrings.
 */

/** Constraint names the natural-key/link guards map from (`meta.target` on P2002). */
export const AP_RECURRING_TEMPLATE_CONSTRAINTS = {
  /** (tenant_id, vendor_id, description_normalized, amount, recurrency_frequency) */
  NATURAL_KEY: 'unq_ap_vendor_recurring_match',
  /** (tenant_id, source_purchase_order_id) */
  SOURCE_PURCHASE_ORDER: 'uq_ap_vendor_recurring_bill_source_purchase_order',
} as const;

export const AP_RECURRING_TEMPLATE_ERROR_CODE_VALUES = [
  // ---- Conflicts (409) — a row already owns the slot the caller is trying to take.
  /** A template with the same vendor + description + amount + frequency already exists. */
  'AP_RECURRING_TEMPLATE_DUPLICATE',
  /** The colliding template exists but is INACTIVE, so it is not offered in selectors. */
  'AP_RECURRING_TEMPLATE_DUPLICATE_INACTIVE',
  /**
   * Same as DUPLICATE, but the caller may not know the colliding template
   * exists — a PAYROLL template outside their confidentiality boundary. Names
   * nothing, and points at the payroll owner for access.
   */
  'AP_RECURRING_TEMPLATE_DUPLICATE_HIDDEN',
  /**
   * The key is taken but the owner could not be identified — a lost insert race
   * whose winner has since changed, or a write from inside a transaction that
   * cannot read the committed state. Names nothing, and does NOT imply payroll
   * confidentiality (which `_HIDDEN`'s copy does).
   */
  'AP_RECURRING_TEMPLATE_DUPLICATE_UNRESOLVED',
  /** Another template already claims this purchase order as its source. */
  'AP_RECURRING_TEMPLATE_PO_ALREADY_LINKED',

  // ---- Missing references (404 / 400)
  'AP_RECURRING_TEMPLATE_NOT_FOUND',
  'AP_RECURRING_TEMPLATE_ENTITY_INVALID',
  'AP_RECURRING_TEMPLATE_BILLS_NOT_FOUND',

  // ---- Payload guards (400)
  'AP_RECURRING_TEMPLATE_ENTITY_REQUIRED',
  'AP_RECURRING_TEMPLATE_PERIOD_LIMIT_REQUIRED',
  'AP_RECURRING_TEMPLATE_PERIOD_LIMIT_INVALID',
  'AP_RECURRING_TEMPLATE_GL_ACCOUNT_REQUIRED',

  // ---- Payroll nature guards (400)
  'AP_RECURRING_TEMPLATE_PAYROLL_FREQUENCY_INVALID',
  'AP_RECURRING_TEMPLATE_PAYROLL_REQUIRES_COLLABORATOR',

  // ---- Merge guards (400 / 404)
  'AP_RECURRING_TEMPLATE_MERGE_EMPTY',
  'AP_RECURRING_TEMPLATE_MERGE_KEEP_IN_SOURCES',
  'AP_RECURRING_TEMPLATE_MERGE_MIXED_NATURES',
  'AP_RECURRING_TEMPLATE_MERGE_KEEP_NOT_FOUND',

  // ---- Bill-link guards (400)
  'AP_RECURRING_TEMPLATE_LINK_UNLINK_CONFLICT',
  'AP_RECURRING_TEMPLATE_LINE_ITEM_MODE_FORBIDDEN',
  'AP_RECURRING_TEMPLATE_STATUTORY_BYPASS_FORBIDDEN',
  'AP_RECURRING_TEMPLATE_STATUTORY_TREATMENT_REQUIRED',

  /**
   * RESERVED — currently unreachable, and deliberately so.
   *
   * The write catches relabel only the faults they can classify (natural-key
   * P2002, entity P2003) and rethrow everything else untouched, so budget,
   * classification and currency errors keep their own handling. That leaves
   * nothing routed here. Kept as the slot for a future explicitly-recognised
   * persistence fault; do NOT wire it up as a catch-all — doing so is what
   * would downgrade `BudgetConcurrencyRetryError`'s 503 retry signal to a 500.
   */
  'AP_RECURRING_TEMPLATE_WRITE_FAILED',
] as const;

export type ApRecurringTemplateErrorCode =
  (typeof AP_RECURRING_TEMPLATE_ERROR_CODE_VALUES)[number];

/**
 * The one id a support engineer needs.
 *
 * `traceId` is generated at the throw site, returned to the caller, shown in
 * the pt-BR copy, and written to `sys_audit_log.log_id`. The Nest filter also
 * sets it as the Sentry tag `recurring_template_trace_id`, so linkage is
 * bidirectional through a single value: an id from a support ticket finds both
 * the stored failure event and the Sentry event, and a Sentry event's tag finds
 * the audit row. There is deliberately no second id to reconcile.
 */
export const AP_RECURRING_TEMPLATE_SENTRY_TAG = 'recurring_template_trace_id';

/** Structured body returned for every code above. */
export interface ApRecurringTemplateErrorPayload {
  traceId: string;
  code: ApRecurringTemplateErrorCode;
  /** Dev-facing cause. English, safe to log, never shown as primary user copy. */
  message: string;
  /** pt-BR statement of what went wrong. */
  userMessage: string;
  /** pt-BR statement of what the user should do next. */
  recoveryHint: string;
  /**
   * Machine-readable recovery affordance the web layer can turn into a button.
   * `none` means the copy in `recoveryHint` is the whole story.
   */
  recovery: ApRecurringTemplateRecovery;
  details?: Record<string, string | number | boolean | null>;
}

export type ApRecurringTemplateRecovery =
  /** Point the user at an existing template they can select. */
  | { kind: 'use_existing_template'; vendorRecurringBillId: string; name: string }
  /** Point the user at an existing template that must be reactivated first. */
  | { kind: 'reactivate_template'; vendorRecurringBillId: string; name: string }
  /** The colliding row is not visible to this caller; only the copy can help. */
  | { kind: 'rename_template' }
  | { kind: 'none' };

/** True when the payload shape matches — used by the web error renderer. */
export function isApRecurringTemplateErrorPayload(
  value: unknown,
): value is ApRecurringTemplateErrorPayload {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.code === 'string' &&
    (AP_RECURRING_TEMPLATE_ERROR_CODE_VALUES as readonly string[]).includes(candidate.code) &&
    typeof candidate.userMessage === 'string' &&
    typeof candidate.traceId === 'string'
  );
}
